import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI, adminAPI } from '../services/api';
import { supabase } from '../services/supabaseClient';

const AuthContext = createContext(null);

// Dashboard.js/Evaluacion.js leen el userId directo de localStorage/sessionStorage
// en vez de por contexto, así que lo espejamos aquí cada vez que hay sesión.
const mirrorUserId = (userId) => {
  if (userId) {
    localStorage.setItem('userId', userId);
  } else {
    localStorage.removeItem('userId');
    sessionStorage.removeItem('userId');
  }
};

// Si la migración de roles no se corrió todavía, mi_rol no existe: tratamos a todos como usuario.
const obtenerRol = async () => {
  try {
    const { data } = await adminAPI.miRol();
    return data || 'usuario';
  } catch (error) {
    console.error('No se pudo obtener el rol:', error);
    return 'usuario';
  }
};

export const AuthProvider = ({ children }) => {
  const [usuario, setUsuario] = useState(null);
  const [rol, setRol] = useState('usuario');
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        setUsuario({ id: session.user.id, email: session.user.email });
        mirrorUserId(session.user.id);
        setRol(await obtenerRol());
      }
      setCargando(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        setUsuario({ id: session.user.id, email: session.user.email });
        mirrorUserId(session.user.id);
        // No se puede hacer await de supabase dentro del callback (bloquea el cliente);
        // se difiere con setTimeout como recomienda supabase-js.
        if (event === 'SIGNED_IN' || event === 'USER_UPDATED') {
          setTimeout(async () => setRol(await obtenerRol()), 0);
        }
      } else {
        setUsuario(null);
        setRol('usuario');
        mirrorUserId(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const login = async (credentials) => {
    try {
      const response = await authAPI.login(credentials);
      const { user } = response.data;
      setUsuario({ id: user.id, email: user.email });
      mirrorUserId(user.id);
      setRol(await obtenerRol());
      return { success: true };
    } catch (error) {
      return { success: false, error: 'Email o contraseña incorrectos' };
    }
  };

  const logout = async () => {
    await authAPI.logout();
    mirrorUserId(null);
    setUsuario(null);
    setRol('usuario');
  };

  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: 'Error al registrar usuario' };
    }
  };

  const esAdmin = rol === 'admin' || rol === 'superadmin';
  const esSuperadmin = rol === 'superadmin';

  return (
    <AuthContext.Provider value={{ usuario, rol, esAdmin, esSuperadmin, login, logout, register, cargando }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return context;
};
